import { runInAction, toJS } from 'mobx';

import type { AddressUsage } from '~/api/DeliveryAddress';
import { DeliveryMethods, type ETACalculation } from '~/api/ETA';
import { company } from '~/company/Company';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { checkoutStore, mainStore, orderStore, userStore } from '~/stores';
import type { DeliveryAddress } from '~/stores/UserStore';
import { partnerPickupAddressRequest } from '~/utils/partnerPickupAddress';

import { deliveryAddressStore } from './DeliveryAddressStore';
import type { EditAddressData } from './interface';

/**
 * The details form with whitespace-only fields treated as empty.
 *
 * A stray space in the house number used to be sent as is: the backend stored
 * it and composed "Building  " out of it, a line nothing could clear
 * afterwards, and once it started answering `null` for such a field instead,
 * the storefront's own fallback line repeated the mistake all the way into
 * the order notes (JS-14749).
 */
function trimAddressFields(data: EditAddressData): EditAddressData {
  return {
    ...data,
    addressBld: data.addressBld.trim(),
    addressFloor: data.addressFloor.trim(),
    addressApt: data.addressApt.trim(),
  };
}

/**
 * House number to save.
 *
 * The form wins once the customer has typed in it — including an empty value,
 * which on tenants where the building is optional is a deliberate "this
 * address has no house number" and used to be silently replaced by whatever
 * the geocoder had resolved. An untouched form still falls back to the
 * resolved address, which is what the PUDO flow — no details form at all —
 * relies on to keep its `pudo` placeholder.
 *
 * `isEditedManually` is read once per save rather than off the store at each
 * call: a save awaits the network between its uses, and the flag can flip
 * meanwhile, which would put two different house numbers into one operation.
 */
function resolveBuilding(
  editAddressData: EditAddressData,
  deliveryAddress: DeliveryAddress,
  isEditedManually: boolean,
): string {
  return isEditedManually
    ? editAddressData.addressBld
    : editAddressData.addressBld || deliveryAddress.addressBld;
}

/**
 * Address line 2 in the shape the backend writes into `street_address_2`
 * ("Building 25, Floor 2, Apartment 7", JS-14749): the guest's local line, and
 * what a saved address is expected to carry when the reply has no line.
 * Whitespace-only fields count as empty.
 */
export function buildAddress2(
  bld?: string,
  floor?: string,
  apt?: string,
): string {
  const building = bld?.trim();
  const level = floor?.trim();
  const flat = apt?.trim();
  const parts = [];

  if (building) {
    parts.push(`Building ${building}`);
  }
  if (level) {
    parts.push(`Floor ${level}`);
  }
  if (flat) {
    parts.push(`Apartment ${flat}`);
  }

  return parts.join(', ');
}

export function sendAddressChangedAnalytics(
  deliveryAddress: DeliveryAddress,
  etaCalculation: ETACalculation | null,
) {
  analyticsService.track(AnalyticsEvent.GENERAL_ADDRESS_CHANGED, {
    postcode: deliveryAddress.zip || '',
    warehouse_code: etaCalculation?.warehouse.code || '',
    source: '',
    eta_min: etaCalculation?.duration.min || 0,
    eta_max: etaCalculation?.duration.max || 0,
    delivery_fee:
      etaCalculation?.cost[DeliveryMethods.COURIER_DELIVERY]?.shippingPounds ||
      0,
    threshold:
      etaCalculation?.cost[DeliveryMethods.COURIER_DELIVERY]?.thresholdPounds ||
      0,
    is_surger: etaCalculation?.highDemand || false,
    lat: deliveryAddress.coordinates.lat || '',
    lon: deliveryAddress.coordinates.lng || '',
  });
}

export function sendPostSaveAnalytics(deliveryAddress: DeliveryAddress) {
  mainStore.sendToRN('trackOneSignalOutcome', {
    name: 'warehouse_selected',
  });

  mainStore.sendToRN('setUserProperties', {
    'Address: postcode': deliveryAddress.zip || '',
  });

  mainStore.sendToRN('setUserProperties', {
    'Address: lat': deliveryAddress.coordinates.lat,
  });

  mainStore.sendToRN('setUserProperties', {
    'Address: lon': deliveryAddress.coordinates.lng,
  });
}

export function sendOnboardingAnalytics(
  deliveryAddress: DeliveryAddress,
  etaCalculation: ETACalculation | null,
) {
  const params = {
    warehouse_code: etaCalculation?.warehouse.code,
    postcode: deliveryAddress.zip || '',
    lat: deliveryAddress.coordinates.lat,
    lon: deliveryAddress.coordinates.lng,
  };

  analyticsService.track(AnalyticsEvent.ONBOARDING_WAREHOUSE_SELECTED, params);
}

export async function updateEtaAndDeliveryMethod() {
  const eta = deliveryAddressStore.etaCalculation;
  const currentMethod = checkoutStore.deliveryMethod;

  const availableMethods = eta?.warehouse.zone
    ? (Object.keys(eta.warehouse.zone) as DeliveryMethods[])
    : [];

  const keepCurrentMethod = availableMethods.includes(currentMethod);
  const targetMethod = keepCurrentMethod
    ? currentMethod
    : deliveryAddressStore.getDeliveryMethodBySelectedZone() ||
      availableMethods[0];

  const methodWillChange = !!targetMethod && targetMethod !== currentMethod;

  // Batch both observable writes so depsHash is recomputed once and the 50ms
  // query-options delay coalesces the changes into a single calculate refetch.
  runInAction(() => {
    orderStore.setEtaCalculation(toJS(eta));
    if (methodWillChange && targetMethod) {
      checkoutStore.setDeliveryMethod(targetMethod);
    }
  });

  // When the method stays the same R11 does not fire — fetch ETA ourselves
  // so slots/prices refresh at save time.
  if (!methodWillChange) {
    await orderStore.requestETA();
  }
}

/**
 * Saves the address the modal collected: PATCH when it already exists, POST
 * otherwise.
 *
 * The details form is deliberately left as the customer filled it. Rewriting
 * `editAddressData` here used to blank every field the moment the request came
 * back — with the modal still open through `fetchAddresses` and the ETA
 * refresh that follow, so the customer watched their values vanish and then
 * reappear once the preload effect re-ran. Nothing needs the reset: this
 * function reads the form from its own argument, and every caller closes the
 * modal afterwards, where `resetStore` clears it.
 *
 * `street_address_2` is not sent: the backend composes it from building, floor
 * and apartment whenever one of them is set and otherwise stores the client's
 * value, so echoing the line of the previous save kept a stale "Building N"
 * on the address and in the courier notes after the house number was cleared
 * (JS-14298, JS-14659). The local `address2` is what the backend is expected
 * to store, used when its reply carries no line or the write was rejected.
 */
export async function saveAuthorizedAddress(
  deliveryAddress: DeliveryAddress,
  formData: EditAddressData,
  options?: {
    alwaysSetDeliveryAddress?: boolean;
    usage?: AddressUsage;
    warehouseCode?: string;
  },
): Promise<{
  addressId: string;
  updatedDeliveryAddress: DeliveryAddress;
  /**
   * Whether the server accepted the write. `userStore.addAddress` and
   * `updateAddress` answer a rejected request with `null` rather than throwing,
   * so without this a caller cannot tell a saved address from one that only
   * exists in this tab.
   */
  persisted: boolean;
}> {
  const editAddressData = trimAddressFields(formData);
  // Only tenants that distinguish fulfilment types tag the address: for
  // everyone else the address contract stays unchanged. `undefined` values are
  // dropped by the JSON serializer, so the keys are simply absent.
  const usage: AddressUsage | undefined = company.tracksAddressUsage
    ? (options?.usage ?? 'delivery')
    : undefined;
  const warehouseCode: string | undefined = company.tracksAddressUsage
    ? (options?.warehouseCode ?? orderStore.etaCalculation?.warehouse.code)
    : undefined;

  const isBuildingEditedManually = deliveryAddressStore.isManuallyInput;

  const effectiveAddressId =
    deliveryAddress.addressId || editAddressData.addressId || '';
  let addressId = effectiveAddressId;
  let updatedDeliveryAddress = deliveryAddress;
  let persisted = true;

  if (effectiveAddressId) {
    const building = resolveBuilding(
      editAddressData,
      deliveryAddress,
      isBuildingEditedManually,
    );
    const address2 = buildAddress2(
      building,
      editAddressData.addressFloor,
      editAddressData.addressApt,
    );
    const updatedAddress = await userStore.updateAddress(effectiveAddressId, {
      street_address_1: deliveryAddress.address1,
      building,
      floor: editAddressData.addressFloor,
      apartment: editAddressData.addressApt,
      postcode: deliveryAddress.zip,
      latitude: deliveryAddress.coordinates.lat,
      longitude: deliveryAddress.coordinates.lng,
      type: editAddressData.type || deliveryAddress.type,
      comment: editAddressData.comment,
      instructions: editAddressData.instructions,
      usage,
      warehouse_code: warehouseCode,
      ...partnerPickupAddressRequest(
        deliveryAddress,
        company.partnerPickupDeliveryProvider,
      ),
    });

    persisted = updatedAddress !== null;

    await userStore.fetchAddresses();

    if (
      options?.alwaysSetDeliveryAddress ||
      userStore.deliveryAddress?.addressId === effectiveAddressId
    ) {
      // What the server confirmed, or — when it rejected the write and
      // userStore.updateAddress swallowed that into a null — what we tried to
      // save. Reading the customer's own fields off the null blanked every one
      // of them on the live address, silently, on every failed save.
      const confirmed = updatedAddress?.data ?? {
        street_address_2: address2,
        building,
        floor: editAddressData.addressFloor,
        apartment: editAddressData.addressApt,
        comment: editAddressData.comment,
        instructions: editAddressData.instructions,
      };

      const mergedAddress: DeliveryAddress = {
        ...deliveryAddress,
        ...editAddressData,
        addressId:
          editAddressData.addressId ?? deliveryAddress.addressId ?? null,
        address2: confirmed.street_address_2 ?? address2,
        addressBld: confirmed.building ?? '',
        addressFloor: confirmed.floor ?? '',
        addressApt: confirmed.apartment ?? '',
        comment: confirmed.comment ?? '',
        instructions: confirmed.instructions ?? [],
        usage,
        warehouseCode,
      };

      updatedDeliveryAddress = mergedAddress;
      // Batch observable writes so calculateDepsHash changes once.
      runInAction(() => {
        userStore.setDeliveryAddress(mergedAddress);
        checkoutStore.setAddress2Val(confirmed.street_address_2 ?? address2);
        checkoutStore.setCommentVal(confirmed.comment ?? '');
      });
    }
  } else {
    const building = resolveBuilding(
      editAddressData,
      deliveryAddress,
      isBuildingEditedManually,
    );
    const floor = editAddressData.addressFloor || deliveryAddress.addressFloor;
    const apartment = editAddressData.addressApt || deliveryAddress.addressApt;
    const address2 = buildAddress2(building, floor, apartment);
    const address = await userStore.addAddress({
      street_address_1: deliveryAddress.address1,
      building,
      floor,
      apartment,
      city: deliveryAddress.city,
      postcode: deliveryAddress.zip,
      latitude: deliveryAddress.coordinates.lat,
      longitude: deliveryAddress.coordinates.lng,
      country: deliveryAddress.country,
      type: editAddressData.type || deliveryAddress.type,
      comment: editAddressData.comment,
      instructions: editAddressData.instructions,
      usage,
      warehouse_code: warehouseCode,
      ...partnerPickupAddressRequest(
        deliveryAddress,
        company.partnerPickupDeliveryProvider,
      ),
    });
    persisted = address !== null;
    addressId = address?.id || '';

    await userStore.fetchAddresses();

    updatedDeliveryAddress = {
      ...deliveryAddress,
      addressId: address?.id || null,
      address2: address?.street_address_2 ?? address2,
      addressBld: address?.building ?? '',
      addressFloor: address?.floor ?? '',
      comment: address?.comment ?? '',
      instructions: address?.instructions ?? [],
    };

    // Batch observable writes so calculateDepsHash changes once.
    runInAction(() => {
      userStore.setDeliveryAddress({
        ...updatedDeliveryAddress,
        ...editAddressData,
        addressId:
          address?.id ??
          editAddressData.addressId ??
          deliveryAddress.addressId ??
          null,
        address2: address?.street_address_2 ?? address2,
        addressBld: address?.building ?? building,
        usage,
        warehouseCode,
      });
      checkoutStore.setAddress2Val(address?.street_address_2 ?? address2);
      checkoutStore.setCommentVal(address?.comment ?? '');
    });
  }

  return { addressId, updatedDeliveryAddress, persisted };
}

export function saveUnauthorizedAddress(
  deliveryAddress: DeliveryAddress,
  formData: EditAddressData,
  usage: AddressUsage = 'delivery',
  warehouseCode?: string,
) {
  const editAddressData = trimAddressFields(formData);
  const isBuildingEditedManually = deliveryAddressStore.isManuallyInput;

  const address2 = buildAddress2(
    editAddressData.addressBld,
    editAddressData.addressFloor,
    editAddressData.addressApt,
  );

  // Same gating as saveAuthorizedAddress, so the local address shape stays
  // unchanged for every other tenant.
  const taggedUsage = company.tracksAddressUsage ? usage : undefined;
  const taggedWarehouseCode = company.tracksAddressUsage
    ? (warehouseCode ?? orderStore.etaCalculation?.warehouse.code)
    : undefined;

  userStore.setDeliveryAddress({
    ...deliveryAddress,
    ...editAddressData,
    addressId: editAddressData.addressId ?? deliveryAddress.addressId ?? null,
    address2,
    addressBld: resolveBuilding(
      editAddressData,
      deliveryAddress,
      isBuildingEditedManually,
    ),
    usage: taggedUsage,
    warehouseCode: taggedWarehouseCode,
  });
}
